import { createSignal } from 'solid-js';
import { localStorageManager } from '~/gateways/storage/localStorageManager';
import { LanguageCode } from '~/use-cases/types/LanguageCode';

type TranslationEnum = Record<string, string>;
type TranslationMap<T extends TranslationEnum> = Record<string, T>;

export type { TranslationEnum, TranslationMap };
export type TranslationKeysType<T extends TranslationEnum> = keyof T;

export function createTranslationKeys<T extends TranslationEnum>(_translationMap: TranslationMap<T>) {
  type Keys = keyof T;
  const keys = Object.keys(_translationMap[Object.keys(_translationMap)[0]]) as Keys[];
  return keys.reduce(
    (acc, key) => {
      acc[key] = key;
      return acc;
    },
    {} as Record<Keys, Keys>
  );
}

export function createTranslationKeysWithType<T extends TranslationEnum>(translationMap: TranslationMap<T>) {
  const keys = createTranslationKeys(translationMap);
  return {
    keys,
    type: {} as TranslationKeysType<T>,
  };
}

const [lang, setLang] = createSignal<string>(localStorageManager.getLanguage() || LanguageCode.English);

export function translate<T extends TranslationEnum>(
  translationMap: TranslationMap<T>,
  language: string,
  key: keyof T,
  params: Record<string, unknown> = {}
): string {
  let text: string = translationMap[language]?.[key] ?? String(key);
  for (const [k, v] of Object.entries(params)) {
    text = text.replaceAll(`{${k}}`, String(v));
  }
  return text;
}

export function useMagicLocalization<T extends TranslationEnum>(translationMap: TranslationMap<T>) {
  type LangCode = keyof typeof translationMap;
  type Keys = keyof T;
  const changeLanguage = (code: LangCode) => {
    setLang(code as string);
    localStorageManager.setLanguage(code as string);
  };

  const t = (key: Keys, params: Record<string, unknown> = {}) => translate(translationMap, lang(), key, params);

  return { t, changeLanguage, currentLanguage: lang };
}
